// Tell `rbCattleTicker` who is asking.
//
// ⚠️ WHY THIS FILE EXISTS, RATHER THAN THREE LINES INSIDE rbCattleTicker.ts.
// That module is shared by the build-time prerender of the homepage and
// deliberately imports no Supabase client. So the dependency runs the other
// way: the app shell pushes the token DOWN into it.
//
//     main.tsx   →  tickerAuth  →  (dynamic) supabase client
//     main.tsx   →  tickerAuth  →  rbCattleTicker.setTickerAuthToken()
//     prerender  →  rbCattleTicker                 (and nothing else)
//
// rbCattleTicker imports nothing new and keeps working under Node, where
// nobody calls the setter and the token stays null.
//
// ── THE PROBLEM THIS IS HALF OF ────────────────────────────────────────────
//
// `rbCattleTicker` fed 54 screens — 12 gated tools and 14 Foote screens among
// them — and identified every caller as anonymous, signed in or not. Any policy
// of the form "answer only if auth.uid() is not null" would blank all of them,
// SILENTLY, because a refusal to a SECURITY INVOKER function returns zero rows
// and no error. Full write-up: `audits/anon-identity-audit-2026-10-03.md`.
//
// This is the carrying half. The gating is a separate, later change, which is
// why landing this on its own must be a visible no-op.

import { setTickerAuthToken } from "@/lib/rbCattleTicker";

/**
 * Resolves once the initial session read has finished — token set or not.
 *
 * ⚠️ WHY THIS EXISTS. attachTickerAuth() is fire-and-forget at startup and
 * loads the Supabase client lazily, so a page that fetches on mount can call
 * the database BEFORE the signed-in token has landed, and the database sees a
 * stranger. The unlisted proof pages (reconciliation, calculation sheet) let
 * an admin in on the session alone — and the owner, signed in as admin, was
 * told he needed a key (2026-10-07). A keyed call awaits this first; callers
 * race it against a short timeout so a page never hangs if attach was never
 * called (tests, prerender).
 */
let markReady: () => void = () => {};
export const tickerAuthReady: Promise<void> = new Promise<void>((resolve) => { markReady = resolve; });

/** True once attachTickerAuth() has been called in this document. */
let attached = false;

/**
 * Wait until the session hand-off has settled, or two seconds, whichever is
 * first — and not at all where nobody attached (the prerender, tests).
 *
 * ⚠️ THE INDEX READERS NEED THIS (Premium lock, 2026-10-13). They now refuse
 * anyone who is not Premium, so a call fired before a paying member's token
 * lands is refused like a stranger's, and the card shows a lock to someone
 * who paid for it.
 */
export function tickerAuthSettled(): Promise<void> {
  if (!attached) return Promise.resolve();
  return Promise.race([tickerAuthReady, new Promise<void>((r) => setTimeout(r, 2000))]);
}

/**
 * Keep the ticker module's token in step with the session.
 *
 * Call once per app shell. Returns the detach.
 *
 * ⚠️ THE CLIENT IS LOADED BY **DYNAMIC** IMPORT, AND THAT IS NOT STYLE.
 * `@/integrations/supabase/client` evaluates `storage: localStorage` at module
 * scope — a bare global that is undefined under Node — so a STATIC import from
 * anything the prerender pass loads throws and fails the build of the company's
 * front door. `src/pages/rbnext/cattle-ticker/SignedInBadge.tsx` already solves
 * it exactly this way and says so in its header; this follows that precedent so
 * the file is safe to import from ANY entry, including the seven prerendered
 * documents, rather than only from the one it happens to be called from today.
 *
 * ⚠️ `getSession()` IS AWAITED **AND** `onAuthStateChange` IS SUBSCRIBED, AND
 * NEITHER IS REDUNDANT. On a hard reload the session is restored from storage
 * asynchronously: subscribing alone leaves the token null through the first
 * paint, which is exactly when the ticker fires its opening requests — so a
 * signed-in user's first load would ask as a stranger. `getSession()` closes
 * that window; subscribing catches sign-in and sign-out afterwards.
 *
 * ⚠️ AND IT SUBSCRIBES **BEFORE** IT AWAITS. The other order drops any event
 * landing during the await — a token refresh on a slow restore — leaving this
 * module holding a token the client has already replaced.
 */
export function attachTickerAuth(): () => void {
  attached = true;
  let unsubscribe: (() => void) | null = null;
  let detached = false;

  void import("@/integrations/supabase/client")
    .then(async ({ supabase }) => {
      if (detached) return;

      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        setTickerAuthToken(session?.access_token ?? null);
      });
      unsubscribe = () => sub.subscription.unsubscribe();

      // Detached while the chunk was in flight — a fast unmount. Undo and stop.
      if (detached) {
        unsubscribe();
        return;
      }

      const { data } = await supabase.auth.getSession();

      // ⚠️ DO NOT CLOBBER A FRESHER TOKEN. If a sign-in or refresh landed while
      // this was in flight, the subscription above already set the real one and
      // this stale read must not overwrite it — which is why this only ever
      // writes a token it actually found, and never writes null.
      if (!detached && data.session?.access_token) {
        setTickerAuthToken(data.session.access_token);
      }
      markReady();
    })
    // A failure here is not fatal: the token stays null and the module behaves
    // exactly as it did before, which is the same path a genuinely anonymous
    // visitor takes.
    .catch(() => { markReady(); });

  return () => {
    detached = true;
    unsubscribe?.();
    setTickerAuthToken(null);
    markReady();
  };
}
